import {FeatureConfig} from '@/config/featureToggles'
import {NodeTypeEnum} from '@/types/NodeTypeEnum'

export const ROUTES = {
  LOGIN: '/cct/login',
  LOGOUT: '/cct/logout',
  HOME: '/',
  ROOT: '/cct',
  LIST_JOURNEYS: '/cct/journeys',
  SUCCESSFUL_LOGIN: '/cct/success',
  CREATE_JOURNEY: '/cct/create',
  EDIT_JOURNEY: '/cct/journey/:id',
  REPORTS: '/cct/reports',
  SETTINGS: '/cct/settings',
  EVENT_NOTIFICATIONS: '/cct/events',
  EVENT_NOTIFICATION: '/cct/events/:id',
  EVENT_NOTIFICATION_EDIT: '/cct/events/:id/edit',
  MESSAGE_TEMPLATES: '/cct/message-templates',
  MESSAGE_TEMPLATE: '/cct/message-templates/:id',
  MESSAGE_TEMPLATE_EDIT: '/cct/message-templates/:id/edit',
  ERROR: '/cct/error',
  ERROR_403: '/cct/error/403',
  ERROR_500: '/cct/error/500',
  SESSION_EXPIRED: '/cct/session-expired',
}

export const BASE_URL_PLACEHOLDER = {
  ADMIN_PORTAL: `{{${FeatureConfig.apiAdminUrl}}}`,
  ADMIN_PORTAL_ENDPOINT: `{{${FeatureConfig.apiAdminUrlEndpointSuffix}}}`,
  EVENTS: `{{${FeatureConfig.apiEventsUrl}}}`,
}
export const ENDPOINTS = {
  // MARK: - Journey Endpoints
  LOAD_JOURNEYS: '/journeys/filter',
  EDIT_JOURNEY: '/journeys/:id',
  SAVE_JOURNEY: '/journeys',
  PUBLISH_JOURNEY: '/journeys/:id/publish',
  JOURNEY_STATUS: '/journeys/:id/status',
  EXPORT_JOURNEY_STATUS_REPORT: '/journeys/:id/export/executions',
  EXPORT_JOURNEY_STATUS_REPORT_DATA: '/journeys/:id/export/executions/data',
  EXPORT_REQUIRES_INTERVENTION: '/journeys/:id/export/requires-intervention',
  EXPORT_REQUIRES_INTERVENTION_DATA: '/journeys/:id/export/requires-intervention/data',
  SETTINGS: '/settings',
  LOAD_STEP_CONFIG: (type: NodeTypeEnum.sendPush | NodeTypeEnum.sendWhatsApp | NodeTypeEnum.sendEmail) => {
    switch (type) {
      case NodeTypeEnum.sendPush:
        return '/step/config/push-notification'
      case NodeTypeEnum.sendWhatsApp:
        return '/step/config/whatsapp'
      case NodeTypeEnum.sendEmail:
        return '/step/config/email'
      default:
        throw new Error(`Unsupported step type: ${type}`)
    }
  },
  LOAD_TRAIT_CATEGORIES: '/traitcategories',
  LOAD_MESSAGE_TEMPLATE_STEP_CONFIG: '/step/config/messagetemplates',
  LOAD_TEMPLATES: '/message-templates/:notificationType',
  ASSIGNED_SEGMENTS: '/segments',
  // MARK: - Admin-Portal Endpoints
  LOAD_USER_ROLES: `${BASE_URL_PLACEHOLDER.ADMIN_PORTAL}/v1/users/auth/${BASE_URL_PLACEHOLDER.ADMIN_PORTAL_ENDPOINT}`,

  // MARK: - Real-Time Events Endpoints
  LOAD_EVENTS: `${BASE_URL_PLACEHOLDER.EVENTS}/eventnotifications`,
  CREATE_EVENT: `${BASE_URL_PLACEHOLDER.EVENTS}/eventnotifications`,
  UPDATE_EVENT: `${BASE_URL_PLACEHOLDER.EVENTS}/eventnotifications/:id`,
  LOAD_EVENT: `${BASE_URL_PLACEHOLDER.EVENTS}/eventnotifications/:id`,
  LOAD_TRAITS_EVENT: `${BASE_URL_PLACEHOLDER.EVENTS}/traitsoperators`,
  LOAD_TRAITS_PAREMETER: `${BASE_URL_PLACEHOLDER.EVENTS}/traitsparametersoperators`,
  LOAD_DYNAMIC_TAG: `${BASE_URL_PLACEHOLDER.EVENTS}/messageparameters`,
  LOAD_COUNTRIES: `${BASE_URL_PLACEHOLDER.EVENTS}/countries`,

  // MARK: - WhatsApp Templates Endpoints
  LOAD_WHATSAPP_TEMPLATES: `/messagetemplates/whatsapp`,
  CREATE_WHATSAPP_TEMPLATE: `/messagetemplates/whatsapp`,
  UPDATE_WHATSAPP_TEMPLATE: `/messagetemplates/whatsapp/:id`,
  GET_WHATSAPP_TEMPLATE: `/messagetemplates/whatsapp/:id`,
  UPDATE_WHATSAPP_TEMPLATE_STATUS: `/messagetemplates/whatsapp/:id`,

  // MARK: - Message Templates Endpoints (channel-generic, e.g. Email Templates)
  LOAD_MESSAGE_TEMPLATES: `/messagetemplates`,
  CREATE_MESSAGE_TEMPLATE: `/messagetemplates`,
  MESSAGE_TEMPLATE_BY_ID: `/messagetemplates/:id`,
}

export const DEFAULT_INTL = 'en-US'

export const STEP_CONFIG = {
  DATA_QUERY_KEY: 'stepConfig',
  DEFAULT_NODE_Y_DISTANCE: 60,
  DEFAULT_ZOOM_LEVEL: 100,
  DEFAULT_OFFSET_POSITION: {
    x: 0,
    y: 0,
  },
  DEFAULT_ENTRY_CONDITION_POSITION: {
    x: 300,
    y: 50,
  },
}

export const JOURNEY_QUERY_KEY = 'journey'
export const JOURNEY_STATUS_EXECUTION_QUERY_KEY = 'journeyStatus'
export const EVENT_NOTIFICATION_DETAILS_QUERY_KEY = 'event-notification-details'

export const FEEDBACK_DURATION = 5 * 1000 // 5 seconds
export const EXECUTION_STATUS_POOLING_INTERVAL = 15 * 1000 // 15 seconds

export const OKTA_AUTH_CONFIG_KEY = 'okta-auth-config'
export const OKTA_TOKEN_STORAGE_KEY = 'okta-token-storage'
export const OKTA_FORBIDDEN_ERROR = 'User is not assigned to the client application.'

export const timezones = {
  UTC: 'UTC',
  AR: 'America/Argentina/Buenos_Aires',
  BR: 'America/Sao_Paulo',
  CL: 'America/Santiago',
  MX: 'America/Mexico_City',
  PL: 'Poland',
  TR: 'Turkey',
  US: 'America/Los_Angeles',
  XX: 'SUT Timezone',
  SK: 'Europe/Bratislava',
  CO: 'America/Bogota',
  DO: 'America/Santo_Domingo',
  RU: 'Europe/Moscow',
  PT: 'Europe/Lisbon',
  RO: 'Europe/Bucharest',
  ES: 'Europe/Madrid',
  PE: 'America/Lima',
  EC: 'America/Guayaquil',
  SA: 'Asia/Riyadh',
  EG: 'Africa/Cairo',
  IN: 'Asia/Kolkata',
  NZ: 'Pacific/Auckland',
  TH: 'Asia/Bangkok',
  NA: 'America/New_York',
  GT: 'America/Guatemala',
  AE: 'Asia/Dubai',
  AIQ: 'America/New_York',
  PGCS: 'America/New_York',
} as const

// Deprecated - we got country id from API
export const countryCodeToId: {[key: string]: number} = {
  CL: 1, // Chile
  MX: 2, // Mexico
  AR: 3, // Argentina
  TR: 4, // Turkey
  CO: 5, // Colombia,
  EC: 6, // Ecuador
  PE: 7, // Peru
  PL: 20, // Poland
  SK: 21, // Slovakia
  CH: 22, // Switzerland
  BE: 23, // Belgium
  FR: 24, // France
  LU: 25, // Luxembourg
  MC: 26, // Monaco
  CY: 27, // Cyprus
  EE: 29, // Estonia
  FI: 30, // Finland
  ES: 40, // Spain
  RU: 41, // Russia
  PT: 42, // Portugal
  RO: 43, // Romania
  DO: 44, // Dominican Republic
  EG: 47, // Egypt
  SA: 48, // Saudi Arabia
}

// Deprecated - we got country name from API
export const countryIdToName: {[key: number]: string} = {
  1: 'Chile',
  2: 'Mexico',
  3: 'Argentina',
  4: 'Turkey',
  5: 'Colombia',
  6: 'Ecuador',
  7: 'Peru',
  20: 'Poland',
  21: 'Slovakia',
  22: 'Switzerland',
  23: 'Belgium',
  24: 'France',
  25: 'Luxembourg',
  26: 'Monaco',
  27: 'Cyprus',
  29: 'Estonia',
  30: 'Finland',
  40: 'Spain',
  41: 'Russia',
  42: 'Portugal',
  43: 'Romania',
  44: 'Dominican Republic',
  47: 'Egypt',
  48: 'Saudi Arabia',
}

export type Timezone = (typeof timezones)[keyof typeof timezones]
